import { useMemo } from 'react';

import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { endOfYear, startOfYear } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { GRAY_SECONDARY } from 'constants/colors';

import { getLocalizedMonthName } from 'utils/date';

import { getDutyLinesSummary } from 'RestAPIs/admin';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, IconButtonProps, IconButton, Tooltip } from '@mui/material';
import {
  useGridApiContext,
  GridRenderCellParams,
  useGridSelector,
  gridFilteredDescendantCountLookupSelector
} from '@mui/x-data-grid-pro';

import { FontAwesomeSvgIcon } from 'components/shared';
import { DataGrid } from 'components/shared/DataGrid';
import { useSelector } from 'hooks';
import YearPeriodSelector from './YearPeriodSelector';
import { useRecoilState } from 'recoil';
import { yearDateRangeAtom } from '../atoms';

export const isNavigationKey = (key: string) =>
  key === 'Home' ||
  key === 'End' ||
  key.indexOf('Arrow') === 0 ||
  key.indexOf('Page') === 0 ||
  key === ' ';

const CustomGridTreeDataGroupingCell = (
  props: GridRenderCellParams & {
    tooltips: { groupingKey: string; title: string }[];
  }
) => {
  const { id, field, rowNode, tooltips } = props;
  const apiRef = useGridApiContext();
  const filteredDescendantCountLookup = useGridSelector(
    apiRef,
    gridFilteredDescendantCountLookupSelector
  );

  const filteredDescendantCount =
    filteredDescendantCountLookup[rowNode.id] ?? 0;

  const handleKeyDown: IconButtonProps['onKeyDown'] = (event) => {
    if (event.key === ' ') {
      event.stopPropagation();
    }
    if (isNavigationKey(event.key) && !event.shiftKey) {
      apiRef.current.publishEvent('cellNavigationKeyDown', props, event);
    }
  };

  const handleClick: IconButtonProps['onClick'] = (event) => {
    apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
    apiRef.current.setCellFocus(id, field);
    event.stopPropagation();
  };

  const tooltip = tooltips.find(
    ({ groupingKey }) => groupingKey === rowNode.groupingKey
  );

  return (
    <Box sx={{ ml: rowNode.depth * 2 }}>
      <Box display="flex" alignItems="center" gap={2}>
        <IconButton
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
          size="small"
          sx={{
            visibility: filteredDescendantCount > 0 ? 'visible' : 'hidden'
          }}
        >
          {rowNode.childrenExpanded ? (
            <KeyboardArrowDownIcon />
          ) : (
            <KeyboardArrowRightIcon />
          )}
        </IconButton>
        <Box display="flex" alignItems="center" gap={1}>
          {rowNode.groupingKey}
          {tooltip && (
            <Tooltip title={tooltip.title}>
              <FontAwesomeSvgIcon icon={faInfoCircle} fontSize="inherit" />
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const DutyLineSummaryGrid = () => {
  const { t } = useTranslation();
  const { hasCostCalculationExternals } = useSelector((state) => state.grants);
  const [yearDateRange] = useRecoilState(yearDateRangeAtom);
  const { data: dutyLineSummary, isLoading } = useQuery(
    ['dutyLineSummary', yearDateRange],
    async () => {
      const { data } = await getDutyLinesSummary({
        startDate: yearDateRange[0] ?? startOfYear(new Date()),
        endDate: yearDateRange[1] ?? endOfYear(new Date()),
        aggregateOn: 'MONTH'
      });

      const periodsWithNrOfRealizedDuties = data.dutiesRealizationSummaryPeriods
        .map((obj) => ({
          ...obj,
          nrOfRealizedDuties:
            obj.totalNrOfDuties -
            obj.nrOfNotRealizedDuties -
            obj.nrOfPNILInvoicedDuties -
            obj.nrOfPNILNotInvoicedDuties
        }))
        .sort((a, b) => a.periodResponse.year - b.periodResponse.year);

      data.dutiesRealizationSummaryPeriods = periodsWithNrOfRealizedDuties;

      return data;
    }
  );

  const data = useMemo(() => {
    if (!dutyLineSummary?.dutiesRealizationSummaryPeriods) {
      return {
        columns: [],
        rows: []
      };
    }

    const { dutiesRealizationSummaryPeriods } = dutyLineSummary;

    const columns = dutiesRealizationSummaryPeriods.map((d, i) => ({
      field: i.toString(),
      minWidth: 110,
      renderHeader: () => (
        <Box>
          {getLocalizedMonthName(d.periodResponse.number - 1)}{' '}
          <Box
            component="span"
            sx={{ fontSize: '10px', color: GRAY_SECONDARY }}
          >
            {d.periodResponse.year}
          </Box>
        </Box>
      )
    }));

    const generateMonthValuesByKey = (key: string) =>
      Array.from(Array(dutiesRealizationSummaryPeriods.length).keys()).reduce(
        (prev, cur) => ({
          ...prev,
          [cur]: dutiesRealizationSummaryPeriods[cur][key]
        }),
        {}
      );

    const rows = [
      {
        id: 0,
        path: [
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizedDuties`)
        ],
        ...generateMonthValuesByKey('nrOfNotRealizedDuties')
      },
      {
        id: 1,
        path: [t(`adminPortal.realization.dutyLineSummary.nrOfRealizedDuties`)],
        ...generateMonthValuesByKey('nrOfRealizedDuties')
      },
      {
        id: 2,
        path: [
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizedDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfRealizableDuties`)
        ],
        ...generateMonthValuesByKey('nrOfRealizableDuties')
      },
      {
        id: 3,
        path: [
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizedDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizableDuties`)
        ],
        ...generateMonthValuesByKey('nrOfNotRealizableDuties')
      },
      {
        id: 4,
        path: [
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizedDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizableDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfNotCalculatedDuties`)
        ],
        ...generateMonthValuesByKey('nrOfNotCalculatedDuties')
      },
      {
        id: 5,
        path: [
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizedDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizableDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfPendingDuties`)
        ],
        ...generateMonthValuesByKey('nrOfPendingDuties')
      },
      {
        id: 6,
        path: [
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizedDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizableDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfBlockedDuties`)
        ],
        ...generateMonthValuesByKey('nrOfBlockedDuties')
      },
      {
        id: 7,
        path: [
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizedDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfNotRealizableDuties`),
          t(`adminPortal.realization.dutyLineSummary.nrOfRejectedDuties`)
        ],
        ...generateMonthValuesByKey('nrOfRejectedDuties')
      }
    ];
    if (hasCostCalculationExternals) {
      rows.push(
        {
          id: 8,
          path: [
            t(
              `adminPortal.realization.dutyLineSummary.nrOfPNILNotInvoicedDuties`
            )
          ],
          ...generateMonthValuesByKey('nrOfPNILNotInvoicedDuties')
        },
        {
          id: 9,
          path: [
            t(`adminPortal.realization.dutyLineSummary.nrOfPNILInvoicedDuties`)
          ],
          ...generateMonthValuesByKey('nrOfPNILInvoicedDuties')
        }
      );
    }
    rows.push({
      id: 12,
      path: [t(`adminPortal.realization.dutyLineSummary.totalNrOfDuties`)],
      ...generateMonthValuesByKey('totalNrOfDuties')
    });

    return {
      columns,
      rows
    };
  }, [dutyLineSummary]);

  return (
    <Box pt={3}>
      <YearPeriodSelector />
      <DataGrid
        loading={isLoading}
        title={t('adminPortal.realization.dutyLineSummary.name')}
        groupingColDef={{
          headerName: t('description'),
          minWidth: 250,
          flex: 1,
          renderCell: (params) => (
            <CustomGridTreeDataGroupingCell
              {...params}
              tooltips={[
                {
                  groupingKey: t(
                    `adminPortal.realization.dutyLineSummary.nrOfRealizableDuties`
                  ),
                  title: t(
                    'adminPortal.realization.dutyLineSummary.tooltips.nrOfRealizableDuties'
                  )
                },
                {
                  groupingKey: t(
                    `adminPortal.realization.dutyLineSummary.nrOfRealizedDuties`
                  ),
                  title: t(
                    'adminPortal.realization.dutyLineSummary.tooltips.nrOfRealizedDuties'
                  )
                },
                {
                  groupingKey: t(
                    `adminPortal.realization.dutyLineSummary.nrOfNotRealizableDuties`
                  ),
                  title: t(
                    'adminPortal.realization.dutyLineSummary.tooltips.nrOfNotRealizableDuties'
                  )
                }
              ]}
            />
          )
        }}
        columns={data?.columns}
        rows={data?.rows}
        treeData
        getTreeDataPath={(row) => row.path}
      />
    </Box>
  );
};

export default DutyLineSummaryGrid;
